"use client";

import { useLingui } from "@lingui/react/macro";
import { CheckIcon } from "@offstreetparking/design-system/icons/CheckIcon";
import { ChevronDownIcon } from "@offstreetparking/design-system/icons/ChevronDownIcon";
import { Menu, MenuItem } from "@offstreetparking/design-system/overlay/Menu";
import { classNames } from "@offstreetparking/design-system/utils";
import { SUPPORTED_LOCALES, SUPPORTED_LOCALE_LABELS, type SupportedLocale } from "@offstreetparking/domain/i18n";
import { usePathname, useRouter } from "next/navigation";
import { localizedPath } from "./localizedPath";

export const LanguageChanger = () => {
	const { i18n, t } = useLingui();
	const currentLocale = i18n.locale;
	const router = useRouter();
	const currentPathname = usePathname();

	const changeLocale = (newLocale: SupportedLocale) => {
		const days = 30;
		const date = new Date();
		date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
		document.cookie = `NEXT_LOCALE=${newLocale};expires=${date.toUTCString()};path=/`;

		router.push(localizedPath({ pathname: currentPathname, locale: newLocale }));
		router.refresh();
	};

	return (
		<Menu
			size="custom"
			anchor="bottom end"
			className="w-40 overflow-hidden py-1"
			trigger={({ open }) => (
				<button
					type="button"
					aria-label={t({
						message: "Language",
						comment: "Accessible name of the language switcher button in the top bar",
					})}
					className="flex items-center gap-1 rounded-md px-2 py-1 font-medium text-neutral text-sm hover:bg-neutral-faded"
				>
					{currentLocale.toUpperCase()}
					<ChevronDownIcon className={classNames("h-4 w-4 transition-transform", open ? "rotate-180" : "")} />
				</button>
			)}
		>
			{SUPPORTED_LOCALES.map((locale) => (
				<MenuItem
					key={locale}
					label={SUPPORTED_LOCALE_LABELS[locale]}
					rightIcon={locale === currentLocale ? CheckIcon : undefined}
					onClick={() => changeLocale(locale)}
				/>
			))}
		</Menu>
	);
};
